import { debounce, Grid, GridSize, GridSpacing, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Breakpoint } from '@mui/material/styles';
import React, { createRef, FunctionComponent, useLayoutEffect, useMemo, useState } from 'react';
import { useResponsive } from '../../utils/customHooks';
import { SkodaSwiper, SkodaSwiperProps } from '../swiper';

export type TilesContainerProps = {
	children: React.ReactElement[];
	modeBreakpoint?: Breakpoint;
	gridModeProps?: Omit<DesktopTilesContainerProps, 'children'>;
	swiperModeProps?: Omit<SkodaSwiperProps, 'children'>;
};

export const TilesContainer: FunctionComponent<TilesContainerProps> = (props) => {
	const { children, modeBreakpoint, gridModeProps, swiperModeProps } = props;
	const theme = useTheme();
	const gridMode = useMediaQuery(theme.breakpoints.up(modeBreakpoint ?? 'sm'));

	if (gridMode) {
		return <DesktopTilesContainer children={children} {...gridModeProps}/>;
	} else {
		return <SkodaSwiper children={children} {...swiperModeProps}/>;
	}
};

type DesktopTilesContainerProps = {
	spacing?: GridSpacing;
	children: React.ReactElement[];
	sameRowHeight?: boolean;
	className?: string;
	gridSizes?: {
		xs?: GridSize;
		sm?: GridSize;
		md?: GridSize;
		lg?: GridSize;
		xl?: GridSize;
	};
};

const DesktopTilesContainer: React.FC<DesktopTilesContainerProps> = (props) => {
	const { children, gridSizes, spacing, className, sameRowHeight } = props;
	const r = useResponsive();
	const resolvedSpacing = spacing ?? r<GridSpacing>({ xs: 4, sm: 5, md: 7, lg: 6, xl: 7 }) ?? 5;
	const refsMemo = useMemo(() => children.map(c => createRef<HTMLDivElement>()), [children]);
	const [itemHeight, setItemHeight] = useState(0);

	useLayoutEffect(() => {
		if (!itemHeight && sameRowHeight) {
			const max = refsMemo.reduce((prev, curr) => {
				const itemHeight = curr.current?.getBoundingClientRect()?.height ?? 0;
				return prev < itemHeight ? itemHeight : prev;
			}, 0);
			setItemHeight(max);
		}
	}, [refsMemo, itemHeight, sameRowHeight]);

	useLayoutEffect(() => {
		if (sameRowHeight) {
			const handleResize = () => setItemHeight(0);
			const debounceHandleResize = debounce(handleResize, 1000);
			window.addEventListener('resize', debounceHandleResize);
			return () => window.removeEventListener('resize', debounceHandleResize);
		}
	}, [sameRowHeight]);

	return (
		<Grid container spacing={resolvedSpacing} className={className}>
			{children.map((tile, index) =>
				<Grid item key={tile.key} ref={refsMemo[index]} style={{ height: sameRowHeight && itemHeight ? itemHeight : undefined }} {...gridSizes}>
					{tile}
				</Grid>)}
		</Grid>
	);
};
